/**
 * Reading-progress ring + back-to-top + next-story controller.
 *
 * Drives three coordinated UI affordances that live above the article:
 *   1. The fixed progress ring (SVG stroke-dashoffset + remaining-mins text)
 *   2. The "back to top" floating button (visible past 400px scroll)
 *   3. The "next story" peek card (appears at 100% scroll, dismissable)
 *
 * Pure helpers are exported separately so unit tests can cover the
 * percent / remaining-mins / ring-text logic in isolation from the DOM,
 * and the integration `init` returns a disposer that detaches every
 * listener (clean teardown for SPA navigation / Playwright assertions).
 *
 * Build path:
 *   src   assets/js/src/single-article/reading-progress.ts
 *   ↓ esbuild (IIFE, target ES2020)
 *   dist  assets/dist/js/single-article/reading-progress.js  (attaches window.XOT.article.progress)
 *
 * Test path:
 *   src   assets/js/src/single-article/reading-progress.ts
 *   ↓ vitest (jsdom env)
 *   test  tests/js/single-article-reading-progress.test.ts
 *
 * @since Phase 2 — JS modernization, bite 6
 */

const RING_CIRCUMFERENCE = 113.1;
const SHOW_RING_MIN_PCT  = 5;
const SHOW_BTT_PX        = 400;
const VIEWPORT_OFFSET    = 0.3; // article scrolls into view when its top crosses 30% of viewport

/**
 * Compute the article reading percent (0–100, clamped + rounded) from the
 * article bounding rect, its total height, and the viewport height.
 *
 * Mirrors the historical formula:
 *   scrolled = -rect.top + viewportHeight * VIEWPORT_OFFSET
 *   pct      = clamp(round((scrolled / articleHeight) * 100), 0, 100)
 *
 * Returns 0 for zero-height articles to avoid NaN.
 */
export function calcReadingPct(rectTop: number, articleHeight: number, viewportHeight: number): number {
    if (articleHeight <= 0) return 0;
    const scrolled = -rectTop + viewportHeight * VIEWPORT_OFFSET;
    const pct      = Math.round((scrolled / articleHeight) * 100);
    return Math.min(100, Math.max(0, pct));
}

/** Remaining minutes given current percent and the article's total minutes. */
export function remainingMinutes(pct: number, totalMins: number): number {
    return Math.max(0, Math.ceil(totalMins * (1 - pct / 100)));
}

/**
 * Format the ring's inner HTML for the current reading state.
 *   pct ≥ 100        → check-mark
 *   remaining ≤ 1    → "1<br><span>min</span>"
 *   otherwise        → "N<br><span>min</span>"
 *
 * Returns pre-built HTML strings to match the live DOM contract — the
 * caller assigns the result to `ringPct.innerHTML`. The style fragments
 * are part of the visual spec and kept inline so the markup is
 * self-contained (no extra rules to enqueue).
 */
export function formatRingText(remaining: number, pct: number): string {
    if (pct >= 100) return '<span style="font-size:.55rem">✓</span>';
    const n = remaining <= 1 ? 1 : remaining;
    return n + '<br><span style="font-size:.38rem;color:rgba(255,255,255,.55)">min</span>';
}

export interface InitOptions {
    /** Total reading minutes — defaults to window.xotReadMins or 3. */
    totalMins?: number;
}

/**
 * Wire the progress ring + back-to-top + next-story to the page DOM.
 * Returns a disposer that removes every event listener attached.
 *
 * Safe to call when any/all DOM refs are missing — each branch checks
 * before touching the element. The scroll listener is `{passive: true}`
 * + rAF-throttled, so it costs nothing measurable per frame.
 */
export function initReadingProgress(opts: InitOptions = {}): () => void {
    if (typeof document === 'undefined') return () => {};

    const articleEl = document.querySelector('.xot-article') as HTMLElement | null;
    const ring      = document.getElementById('xot-progress-ring');
    const ringFill  = document.getElementById('xot-ring-fill');
    const ringPct   = document.getElementById('xot-ring-pct');
    const bttBtn    = document.getElementById('back-to-top');
    const nextStory = document.getElementById('xot-next-story');
    const nextClose = document.getElementById('xot-next-close');

    const totalMins = opts.totalMins
        ?? (typeof (window as { xotReadMins?: number }).xotReadMins === 'number'
            ? (window as { xotReadMins?: number }).xotReadMins!
            : 3);

    let scrollTick    = false;
    let nextDismissed = false;

    function onScroll(): void {
        if (scrollTick) return;
        scrollTick = true;
        requestAnimationFrame(() => {
            if (!articleEl) { scrollTick = false; return; }
            const rect = articleEl.getBoundingClientRect();
            const pct  = calcReadingPct(rect.top, articleEl.offsetHeight, window.innerHeight);

            // Ring visibility — visible between SHOW_RING_MIN_PCT and 100
            if (ring) {
                ring.classList.toggle('visible', pct > SHOW_RING_MIN_PCT && pct < 100);
                ring.setAttribute('aria-valuenow', String(pct));
            }

            // Ring fill — stroke-dashoffset across the SVG circumference
            if (ringFill) {
                ringFill.style.strokeDashoffset = String(RING_CIRCUMFERENCE * (1 - Math.min(pct, 100) / 100));
            }

            // Ring inner text — N mins remaining or check-mark
            if (ringPct) ringPct.innerHTML = formatRingText(remainingMinutes(pct, totalMins), pct);

            // Next-story peek — appears at 100%, hidden again if the user scrolls back up
            if (nextStory && !nextDismissed) nextStory.classList.toggle('visible', pct >= 100);

            // Back-to-top — appears past SHOW_BTT_PX scrollY
            if (bttBtn) bttBtn.classList.toggle('visible', window.scrollY > SHOW_BTT_PX);

            scrollTick = false;
        });
    }

    function onBackToTop(): void {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function onNextDismiss(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
        nextStory?.classList.remove('visible');
        nextDismissed = true;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    bttBtn?.addEventListener('click', onBackToTop);
    nextClose?.addEventListener('click', onNextDismiss);

    return function dispose(): void {
        window.removeEventListener('scroll', onScroll);
        bttBtn?.removeEventListener('click', onBackToTop);
        nextClose?.removeEventListener('click', onNextDismiss);
    };
}

/* ── window.XOT.article.progress attachment (legacy IIFE consumers) ── */
interface XotArticleProgress {
    init:             typeof initReadingProgress;
    calcReadingPct:   typeof calcReadingPct;
    remainingMinutes: typeof remainingMinutes;
    formatRingText:   typeof formatRingText;
}

declare global {
    interface Window {
        xotReadMins?: number;
        XOT?: {
            article?: {
                text?:      unknown;
                highlight?: unknown;
                progress?:  XotArticleProgress;
            };
            [k: string]: unknown;
        };
    }
}

if (typeof window !== 'undefined') {
    const xot = (window.XOT ?? {}) as NonNullable<Window['XOT']>;
    const article = (xot.article ?? {}) as NonNullable<NonNullable<Window['XOT']>['article']>;
    article.progress = {
        init:             initReadingProgress,
        calcReadingPct,
        remainingMinutes,
        formatRingText,
    };
    xot.article = article;
    window.XOT  = xot;
}
